import { FC, useEffect, useMemo, useState } from "react";

import CustomSelect from "../../../../components/select/Select";
import { SelectOptionType } from "../../../../types/types";
import {
  REMINDER_DATE_BASE_OPTIONS,
  REMINDER_DIRECTION_OPTIONS,
  ReminderDraft,
} from "./reminders.mock";
import {
  filterUserOptions,
  formatReminderDate,
  getReminderDate,
} from "./reminders.utils";
import { useReminderLookups } from "./useReminderLookups";

const findSelectedOption = (
  options: SelectOptionType[],
  value?: string,
): SelectOptionType | null => {
  if (!value) return null;

  return options.find((option) => option.value === value) ?? null;
};

const getSingleSelectValue = (
  value: SelectOptionType | readonly SelectOptionType[],
) => {
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }

  return value;
};

type Props = {
  isOpen: boolean;
  initialDraft?: ReminderDraft;
  isSaving?: boolean;
  lookups: ReturnType<typeof useReminderLookups>;
  work: { startDate?: Date | string | null; dueDate?: Date | string | null };
  onClose: () => void;
  onSave: (draft: ReminderDraft) => void;
  title?: string;
  submitLabel?: string;
};

export const AddReminderModal: FC<Props> = ({
  isOpen,
  initialDraft,
  isSaving = false,
  lookups,
  work,
  onClose,
  onSave,
  title = "Reminder",
  submitLabel = "Create",
}) => {
  const [draft, setDraft] = useState<ReminderDraft>({
    days: "5",
    direction: "after",
    dateBase: "dueDate",
    notifyUserId: "",
  });
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setUserSearch("");
    setDraft(
      initialDraft || {
        days: "5",
        direction: "after",
        dateBase: "dueDate",
        notifyUserId: "",
      },
    );
  }, [initialDraft, isOpen]);

  const goesOffAt = useMemo(() => getReminderDate(draft, work), [draft, work]);
  // A reminder whose day has already passed would never be sent.
  const isInPast = Boolean(goesOffAt && goesOffAt.getTime() < Date.now());

  const canSave = useMemo(() => {
    const days = Number(draft.days);

    return (
      days > 0 &&
      !isInPast &&
      Boolean(draft.direction && draft.dateBase && draft.notifyUserId)
    );
  }, [draft, isInPast]);

  const handleSelectChange =
    (field: keyof ReminderDraft) =>
    (value: SelectOptionType | readonly SelectOptionType[]) => {
      const option = getSingleSelectValue(value);

      setDraft((prev) => ({
        ...prev,
        [field]: option?.value ?? "",
      }));
    };

  const handleSave = () => {
    if (!canSave || isSaving) return;

    onSave(draft);
  };

  const renderSelect = ({
    options,
    value,
    placeholder,
    onChange,
    width = "180px",
    isLoading,
    withAvatar,
    inputValue,
    onChangeInput,
  }: {
    options: SelectOptionType[];
    value: string;
    placeholder: string;
    onChange: (value: SelectOptionType | readonly SelectOptionType[]) => void;
    width?: string;
    isLoading?: boolean;
    withAvatar?: boolean;
    inputValue?: string;
    onChangeInput?: (value: string) => void;
  }) => (
    <CustomSelect
      options={options}
      value={findSelectedOption(options, value)}
      onChange={onChange}
      placeholder={placeholder}
      isLoading={isLoading}
      inputValue={inputValue}
      onChangeInput={onChangeInput}
      withMenu
      withAvatar={withAvatar}
      selectStyle={{ fullWidth: true, fontWeight: 400, width }}
    />
  );

  if (!isOpen) return null;

  return (
    <div className={"modal modal-open"}>
      <div className={"modal-box w-[450px] max-w-[calc(100vw-32px)] p-0"}>
        <div
          className={
            "mb-4 flex w-full items-center justify-start border-b border-gray-200 px-4 py-3 text-base font-semibold"
          }>
          {title}
        </div>

        <div className={"mb-5 px-6"}>
          <div className={"mb-2 text-sm font-semibold"}>When</div>

          <div className={"flex items-center gap-2"}>
            <input
              type={"number"}
              min={1}
              className={"input input-bordered h-[38px] w-[70px] text-sm"}
              value={draft.days}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, days: event.target.value }))
              }
            />

            <span className={"whitespace-nowrap text-sm"}>Day(s)</span>

            <div className={"w-[110px]"}>
              {renderSelect({
                options: REMINDER_DIRECTION_OPTIONS,
                value: draft.direction,
                placeholder: "Select",
                onChange: handleSelectChange("direction"),
                width: "110px",
              })}
            </div>

            <span className={"text-sm"}>the</span>

            <div className={"w-[140px]"}>
              {renderSelect({
                options: REMINDER_DATE_BASE_OPTIONS,
                value: draft.dateBase,
                placeholder: "Select date",
                onChange: handleSelectChange("dateBase"),
                width: "140px",
              })}
            </div>
          </div>
        </div>

        {goesOffAt && (
          <div
            className={`-mt-2 mb-5 px-6 text-xs ${
              isInPast ? "text-[#DC2626]" : "text-gray-500"
            }`}>
            {isInPast
              ? `That's ${formatReminderDate(
                  goesOffAt,
                )}, which has already passed. Choose a later day.`
              : `Goes off on ${formatReminderDate(goesOffAt)} at 9:00 AM.`}
          </div>
        )}

        <div className={"mb-8 px-6"}>
          <div className={"mb-2 text-sm font-semibold"}>Notify</div>

          {renderSelect({
            options: filterUserOptions(lookups.userOptions, userSearch),
            value: draft.notifyUserId,
            inputValue: userSearch,
            onChangeInput: setUserSearch,
            placeholder: "Select user",
            onChange: handleSelectChange("notifyUserId"),
            width: "402px",
            isLoading: lookups.isLoadingUsers,
            withAvatar: true,
          })}
        </div>

        <div
          className={
            "mt-4 flex justify-center gap-3 p-3 text-sm font-semibold"
          }>
          <button
            type={"button"}
            onClick={onClose}
            className={
              "flex h-[32px] w-[100px] items-center justify-center rounded-lg border border-[#C8D2DE] px-4 py-2 font-Manrope text-sm text-[#253747]"
            }>
            Cancel
          </button>

          <button
            type={"button"}
            disabled={!canSave}
            onClick={handleSave}
            className={
              "flex h-[32px] w-[120px] items-center justify-center rounded-lg bg-[#7c66f0] px-4 py-2 font-Manrope text-sm text-white disabled:opacity-75"
            }>
            {isSaving ? "Saving" : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
